import React from 'react';
import { useMutation } from '@apollo/client';
import { Badge, Button, Table } from '@odin-labs/components';
import {
  GetWorkerCardsDocument,
  useGetWorkerCardsQuery,
  WorkerCardType,
  CardStatus,
  JobsiteWorkerOrientationStatus,
  MutationReissueMobileBadgeArgs,
  Success,
  useGetCurrentCardPrintJobQuery,
} from 'apollo/generated/client-operations';
import { WorkerCard } from 'graphql/server/queries/types';
import { to } from 'acl';
import { AuthContext } from 'auth';
import { Card, CardHeader, CardHeaderTitle } from 'components/reactstrap';
import { AlertService } from 'components/alertService';
import { LoadingError } from 'components/loadingError';
import { LockedFeatureAlert } from 'components/lockedFeatureAlert';
import { generateApiUrl } from 'config/app';
import { REISSUE_MOBILE_BADGE } from 'containers/worker/helpers/queries';
import { getOnboardingModule } from 'containers/worker/helpers/utils';
import { BadgeModal, DeactivateBadgeModal } from 'containers/worker/modals';
import { getJobsitesOptions } from 'containers/worker/modals/editJobsiteWorkerModal/utils';
import {
  BadgingStep,
  WorkerTabProps,
  CardPrintJob,
} from 'containers/worker/types';
import { useAvailableJobsites } from 'graphql/client/useAvailableJobsites';
import { isQueryLoading, usePrevious, useResettableState } from 'utils';
import { getGraphQLError } from 'utils/error';
import { useLegacyState } from 'utils/useLegacyState';
import { PlusIcon } from 'components/icons';
import { useFlags } from 'featureFlags';
import { LockedWorkerAlert } from 'containers/worker/tabs/LockedWorkerAlert';
import {
  BadgeModalState,
  BadgeModalUpdateValue,
} from 'containers/worker/modals/badgeModal/types';
import { WorkerBadgesAlerts } from './WorkerBadgesAlerts';
import { BadgeDetailsDrawer } from './BadgeDetailsDrawer';
import {
  getJobsiteAccessHistoryColumns,
  getWorkerCardTableColumns,
} from './WorkerBadges.tables';

type WorkerBadgesProps = WorkerTabProps & {
  onChange?: () => void;
};

export function WorkerBadges(props: WorkerBadgesProps): React.ReactElement {
  const {
    worker,
    jobsiteWorkers,
    jobsiteNames,
    showJobsiteAccessHistory = true,
    isLocked,
    refetchWorkerData,
    updateWorkerState,
    onTabApiChange,
    onChange,
  } = props;
  const { workerId } = worker ?? {};

  const {
    value: workerCardToDeactivate,
    setValue: openDeactivateBadgeModal,
    resetValue: closeDeactivateBadgeModal,
  } = useResettableState<WorkerCard>(null, null);

  const [currentCardPrintJob, setCurrentCardPrintJob] =
    React.useState<CardPrintJob>(null);

  const [badgeModalState, setBadgeModalSate] = useLegacyState<BadgeModalState>({
    isOpen: false,
    workerCardToReactivate: null,
    workerCardToReprint: null,
  });
  const updateBadgeModalState = React.useCallback(
    (updValue: BadgeModalUpdateValue) =>
      setBadgeModalSate((prevState) => ({
        prevBadgingStep: prevState.badgingStep,
        ...updValue,
      })),
    [setBadgeModalSate],
  );
  const openBadgeModal = React.useCallback(
    (
      badgingStep: BadgingStep,
      extra?: Pick<
        BadgeModalUpdateValue,
        'workerCardToReactivate' | 'workerCardToReprint'
      >,
    ) =>
      setBadgeModalSate((prevState) => ({
        prevBadgingStep: prevState.badgingStep,
        badgingStep,
        ...extra,
        isOpen: true,
      })),
    [setBadgeModalSate],
  );
  const closeBadgeModal = React.useCallback(
    () =>
      setBadgeModalSate({
        isOpen: false,
        workerCardToReactivate: null,
        workerCardToReprint: null,
      }),
    [setBadgeModalSate],
  );
  const {
    badgingStep,
    prevBadgingStep,
    workerCardToReactivate,
    workerCardToReprint,
    expirationDate,
    isOpen: isAddBadgeModalOpen,
  } = badgeModalState;

  const { currentUser: user } = React.useContext(AuthContext);
  const { connectionsEnabled } = useFlags();
  const canSeeBadgeConnections =
    connectionsEnabled && user.isAllowed(to.seeWorkerProfileActions);
  const {
    value: badgeToView,
    setValue: openBadgeDetails,
    resetValue: closeBadgeDetails,
  } = useResettableState<WorkerCard>(null, null);
  const isContractorUser = user.isContractor;
  const isFeatureLocked = !user.hasPaidAccess;

  const {
    data: workerCardsData,
    error: workerCardsError,
    networkStatus: workerCardsNetworkStatus,
    refetch: refetchWorkerCards,
  } = useGetWorkerCardsQuery({
    fetchPolicy: 'network-only',
    pollInterval: 60000,
    notifyOnNetworkStatusChange: true,
    skip: isLocked || isFeatureLocked || !workerId,
    variables: {
      workerId,
      workerCardsInput: {
        includeArchived: true,
        syncPrintJob: true,
      },
    },
  });

  const workerCardIds = workerCardsData?.getWorker.workerCards.edges
    .map(({ node }) => node.workerCardId)
    .sort()
    .join();

  const previousWorkerCardIds = usePrevious(workerCardIds);
  React.useEffect(() => {
    if (
      previousWorkerCardIds !== undefined &&
      workerCardIds &&
      previousWorkerCardIds !== workerCardIds
    ) {
      onChange?.();
    }
  }, [workerCardIds, previousWorkerCardIds, onChange]);

  const { workerCards, badges, hasExtraActiveCards, hasExtraActiveQRCodes } =
    React.useMemo(() => {
      // Filter out failed cards from showing up in the UI
      const validWorkerCards = workerCardsData?.getWorker.workerCards.edges
        .map(({ node }) => node)
        .filter((wc) => wc.cardStatus !== CardStatus.Failed);

      return {
        workerCards: validWorkerCards,
        badges: validWorkerCards?.filter(
          ({ workerCardFormat: { cardType } }) =>
            cardType !== WorkerCardType.Bluetooth,
        ),
        hasExtraActiveCards:
          validWorkerCards?.filter(
            ({ workerCardFormat, cardStatus }) =>
              workerCardFormat?.cardType === WorkerCardType.Proximity &&
              cardStatus === CardStatus.Active,
          ).length > 1,
        hasExtraActiveQRCodes:
          validWorkerCards?.filter(
            ({ workerCardFormat, cardStatus }) =>
              workerCardFormat?.cardType === WorkerCardType.QrCode &&
              cardStatus === CardStatus.Active,
          ).length > 1,
      };
    }, [workerCardsData]);

  const { data: currentCardPrintJobData, refetch: refetchCurrentCardPrintJob } =
    useGetCurrentCardPrintJobQuery({
      fetchPolicy: 'network-only',
      pollInterval: 4000,
      // pollInterval: window.location.href.includes('.local.')
      //   ? undefined
      //   : 4000,
      skip: !workerId,
      variables: {
        workerId,
      },
    });

  React.useEffect(() => {
    /**
     * The CreateNewBadgeModalContainer can also set the current card print job (the setter is passed)
     * And there doesn't seem to be a hook connected to polling completion, so use a separate
     * currentCardPrintJob to keep things straight
     */
    setCurrentCardPrintJob(currentCardPrintJobData?.getCurrentCardPrintJob);
  }, [currentCardPrintJobData]);

  // Only for PVC cards, refetch worker cards when print job status changes
  // to update the UI with the latest print job card status
  const prevCardPrintJobsStatus = usePrevious(currentCardPrintJob?.status);
  React.useEffect(() => {
    if (
      prevCardPrintJobsStatus &&
      currentCardPrintJob?.workerCard?.workerCardFormat?.cardType ===
        WorkerCardType.Pvc
    ) {
      refetchWorkerCards();
    }
  }, [currentCardPrintJob?.status]);

  const {
    jobsites: userJobsites,
    loading: userJobsitesLoading,
    error: userJobsitesError,
  } = useAvailableJobsites();

  const openReactivateBadgeModal = React.useCallback(
    (workerCard: WorkerCard): void => {
      openBadgeModal(BadgingStep.ReactivateBadge, {
        workerCardToReactivate: {
          id: workerCard.workerCardId,
          cardType: workerCard.workerCardFormat.cardType,
        },
      });
    },
    [openBadgeModal],
  );

  const openReprintBadgeModal = React.useCallback(
    (workerCard: WorkerCard): void => {
      openBadgeModal(BadgingStep.Print, {
        workerCardToReprint: {
          id: workerCard.workerCardId,
          cardType: workerCard.workerCardFormat.cardType,
        },
      });
    },
    [openBadgeModal],
  );

  const [reissueMobileBadge] = useMutation<
    Success,
    MutationReissueMobileBadgeArgs
  >(REISSUE_MOBILE_BADGE, {
    onCompleted: () => {
      AlertService.alert('success', 'Success!', '');
    },
    onError: (error) => {
      AlertService.alert(
        'danger',
        'Something went wrong!',
        getGraphQLError(error),
      );
    },
    refetchQueries: [GetWorkerCardsDocument],
  });

  const submitIssueMobileBadge = React.useCallback(
    async (
      workerCard: WorkerCard,
      target?: 'worker' | 'apple' | 'google',
    ): Promise<void> => {
      const { workerCardId } = workerCard;
      if (target === 'apple' || target === 'google') {
        const url = generateApiUrl(
          'wallet',
          target,
          'mobile-badge',
          'generate',
          workerId,
          workerCardId,
        );
        window.open(url, '_blank');
      } else {
        await reissueMobileBadge({
          variables: {
            workerCardId,
            newPhoneNumber: null,
          },
        });
      }
    },
    [reissueMobileBadge, workerId],
  );

  const { allowedToPrintJobsites, jobsitesOptions, isAddBadgeAvailable } =
    React.useMemo(() => {
      const allowedJobsiteAccesses = jobsiteWorkers?.filter((jobsiteWorker) => {
        const { jobsiteContractor, orientationStatus } = jobsiteWorker;
        const { jobsite } = jobsiteContractor;
        const { badgeAssignmentAllowed, badgePrintingAllowed } =
          getOnboardingModule(jobsite.modules) ?? {};

        const userJobsiteIds = userJobsites?.map((j) => j.jobsiteId);

        return (
          userJobsiteIds?.includes(jobsite.jobsiteId) &&
          ((badgeAssignmentAllowed ?? true) ||
            badgePrintingAllowed ||
            orientationStatus === JobsiteWorkerOrientationStatus.Complete)
        );
      });
      const allowedJobsites = allowedJobsiteAccesses?.map(
        (jw) => jw.jobsiteContractor.jobsite,
      );
      const allowedJobsiteIds = allowedJobsites.map((j) => j.jobsiteId);

      return {
        allowedToPrintJobsites: allowedJobsites,
        jobsitesOptions: getJobsitesOptions(allowedJobsites),
        isAddBadgeAvailable: userJobsites
          ?.filter((j) => allowedJobsiteIds.includes(j.jobsiteId))
          .some((j) => j.jobsiteCardFormats?.edges?.length),
      };
    }, [jobsiteWorkers, userJobsites]);

  const badgesColumns = React.useMemo(
    () =>
      getWorkerCardTableColumns({
        openReactivateBadgeModal,
        openDeactivateBadgeModal,
        openReprintBadgeModal,
        submitIssueMobileBadge,
        user,
      }),
    [
      openReactivateBadgeModal,
      openDeactivateBadgeModal,
      openReprintBadgeModal,
      submitIssueMobileBadge,
      user,
    ],
  );

  const jobsiteAccessHistoryColumns = React.useMemo(
    () => getJobsiteAccessHistoryColumns(),
    [],
  );

  const jobsiteAccessHistory = React.useMemo(
    () =>
      worker?.accessHistory?.filter((access) =>
        jobsiteNames?.includes(access.jobsiteName),
      ) ?? [],
    [worker?.accessHistory, jobsiteNames],
  );

  React.useEffect(
    () => onTabApiChange?.({ refetchData: refetchWorkerData }),
    [refetchWorkerData],
  );

  if (isLocked) {
    return <LockedWorkerAlert worker={worker} />;
  }

  const error = workerCardsError ?? userJobsitesError;
  if (error) {
    return <LoadingError error={error} />;
  }

  if (isFeatureLocked) {
    return (
      <div className="odin-p-8">
        <LockedFeatureAlert />
      </div>
    );
  }

  const loading =
    isQueryLoading(workerCardsNetworkStatus) || userJobsitesLoading;

  return (
    <div>
      <div className="odin-space-y-3">
        <WorkerBadgesAlerts
          currentCardPrintJob={currentCardPrintJob}
          openBadgeModal={openBadgeModal}
          hasExtraActiveCards={hasExtraActiveCards}
          hasExtraActiveQRCodes={hasExtraActiveQRCodes}
        />
        <Card>
          <CardHeader className="!odin-border-b-0">
            <div className="odin-flex odin-items-center odin-justify-between odin-w-full">
              <div>
                <CardHeaderTitle as="h3" className="odin-mr-1 odin-inline">
                  Badges
                </CardHeaderTitle>
                <Badge text={badges?.length.toString()} color="gray" />
              </div>
              <div>
                {isAddBadgeAvailable &&
                user.isAllowed(to.addBadges) &&
                jobsitesOptions.length &&
                !isContractorUser ? (
                  <Button
                    icon={PlusIcon}
                    size={['base', 'md:sm']}
                    onClick={(): void => {
                      openBadgeModal(BadgingStep.ChooseBadgeType);
                    }}
                    text="Add Badge"
                    hideTextOnMobile
                  />
                ) : null}
              </div>
            </div>
          </CardHeader>
          <Table
            columns={badgesColumns}
            data={badges}
            loading={loading}
            enablePagination={false}
            remote
            cellClassName="!odin-pl-5"
            disableGlobalFilter
            disableSortBy
            onRowClick={
              canSeeBadgeConnections
                ? ({ data: badge }: { data: WorkerCard }): void =>
                    openBadgeDetails(badge)
                : undefined
            }
          />
        </Card>
        {showJobsiteAccessHistory && (
          <Card>
            <CardHeader className="!odin-border-b-0">
              <CardHeaderTitle as="h3">Jobsite access history</CardHeaderTitle>
            </CardHeader>
            <Table
              columns={jobsiteAccessHistoryColumns}
              data={jobsiteAccessHistory}
              loading={loading}
              noResultsText="No jobsite access history to show"
              enablePagination={false}
              remote
              disableGlobalFilter
              disableSortBy
            />
          </Card>
        )}
      </div>
      <BadgeModal
        isOpen={isAddBadgeModalOpen}
        badgingStep={badgingStep}
        prevBadgingStep={prevBadgingStep}
        updateBadgeModalState={updateBadgeModalState}
        worker={worker}
        jobsites={allowedToPrintJobsites}
        jobsiteWorkers={jobsiteWorkers}
        currentCardPrintJob={currentCardPrintJob}
        workerCards={workerCards}
        workerCardToReactivate={workerCardToReactivate}
        workerCardToReprint={workerCardToReprint}
        expirationDate={expirationDate}
        refetchWorkerCards={refetchWorkerCards}
        closeModal={closeBadgeModal}
        setCurrentCardPrintJob={setCurrentCardPrintJob}
        refetchWorker={refetchWorkerData}
        refetchCurrentCardPrintJob={refetchCurrentCardPrintJob}
        updateWorkerState={updateWorkerState}
      />
      <BadgeDetailsDrawer badge={badgeToView} onClose={closeBadgeDetails} />
      <DeactivateBadgeModal
        isOpen={!!workerCardToDeactivate}
        closeModal={closeDeactivateBadgeModal}
        onConfirm={closeDeactivateBadgeModal}
        workerCardId={workerCardToDeactivate?.workerCardId}
      />
    </div>
  );
}
